import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Bell, CheckCheck, Circle, Trash2, Square, CheckSquare, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import ConfirmDialog from "@/components/ConfirmDialog";

const typeMeta = {
  prezzi: { color: "text-blue-600 bg-blue-50", label: "Prezzi" },
  report: { color: "text-purple-600 bg-purple-50", label: "Report" },
  news: { color: "text-amber-600 bg-amber-50", label: "News" },
  sistema: { color: "text-slate-600 bg-slate-100", label: "Sistema" },
};

export default function Notifiche() {
  const { user } = useAuth();
  const canManage = user?.role === "admin" || user?.role === "editor";
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [confirmState, setConfirmState] = useState(null);

  const load = async () => {
    const { data } = await api.get("/notifications");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const markAll = async () => {
    await api.post("/notifications/read-all");
    window.dispatchEvent(new CustomEvent("notifications:changed"));
    load();
  };
  const markOne = async (id) => {
    await api.post(`/notifications/${id}/read`);
    window.dispatchEvent(new CustomEvent("notifications:changed"));
    load();
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    const ids = items.map((n) => n.id);
    const allSelected = ids.length > 0 && ids.every((id) => selected.has(id));
    setSelected(allSelected ? new Set() : new Set(ids));
  };
  const clearSelection = () => setSelected(new Set());

  const bulkDelete = () => {
    if (selected.size === 0) return;
    setConfirmState({
      title: `Eliminare ${selected.size} notific${selected.size === 1 ? "a" : "he"}?`,
      message: "Le notifiche selezionate verranno rimosse per tutti gli utenti.",
      confirmLabel: "Elimina tutte",
      variant: "danger",
      onConfirm: async () => {
        setBusy(true);
        try {
          const { data } = await api.post("/notifications/bulk-delete", { ids: Array.from(selected) });
          toast.success(`${data.deleted} notifiche eliminate`);
          clearSelection();
          window.dispatchEvent(new CustomEvent("notifications:changed"));
          await load();
        } catch (e) {
          toast.error(e.response?.data?.detail || "Errore eliminazione");
        } finally {
          setBusy(false);
        }
      },
    });
  };

  const allSelected = items.length > 0 && items.every((n) => selected.has(n.id));

  return (
    <div className="space-y-5 max-w-3xl">
      <ConfirmDialog
        state={confirmState}
        onClose={() => setConfirmState(null)}
      />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Notifiche</h1>
          <p className="text-sm text-slate-500 mt-1">
            {canManage
              ? "Storico completo. Puoi eliminare le notifiche vecchie per mantenere la lista pulita."
              : "Vengono mostrate le ultime 20 notifiche a te destinate."}
          </p>
        </div>
        <button onClick={markAll} className="text-sm border border-slate-200 hover:border-blue-500 hover:text-blue-600 bg-white rounded-lg px-3 py-2 font-semibold flex items-center gap-2" data-testid="mark-all-read">
          <CheckCheck size={15} /> Segna tutte come lette
        </button>
      </div>

      {/* Bulk toolbar — solo per admin/editor */}
      {canManage && items.length > 0 && (
        <div className="flex items-center gap-3 flex-wrap text-sm">
          <button
            onClick={toggleSelectAll}
            className="flex items-center gap-1.5 text-slate-600 hover:text-blue-600 font-medium"
            data-testid="notif-select-all"
          >
            {allSelected ? <CheckSquare size={16} className="text-blue-600" /> : <Square size={16} />}
            {allSelected ? "Deseleziona tutto" : "Seleziona tutto"}
          </button>
          {selected.size > 0 && (
            <>
              <span className="text-slate-400">·</span>
              <span className="font-semibold text-blue-900">{selected.size} selezionat{selected.size === 1 ? "a" : "e"}</span>
              <button
                onClick={bulkDelete}
                disabled={busy}
                className="ml-2 bg-red-600 hover:bg-red-700 text-white rounded-lg px-3 py-1.5 font-semibold flex items-center gap-1.5 disabled:opacity-60"
                data-testid="notif-bulk-delete"
              >
                {busy ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                Elimina selezionate
              </button>
              <button
                onClick={clearSelection}
                className="text-slate-500 hover:text-slate-900 flex items-center gap-1"
                data-testid="notif-bulk-clear"
              >
                <X size={13} /> Annulla
              </button>
            </>
          )}
        </div>
      )}

      <div className="space-y-2">
        {items.length === 0 && <div className="card-elevated p-8 text-center text-slate-500">Nessuna notifica</div>}
        {items.map((n) => {
          const meta = typeMeta[n.type] || typeMeta.sistema;
          const isSelected = selected.has(n.id);
          return (
            <div
              key={n.id}
              className={`card-elevated p-4 flex gap-4 items-start ${!n.is_read ? "border-l-4 border-blue-500" : ""} ${isSelected ? "ring-2 ring-blue-300 bg-blue-50/60" : ""}`}
              data-testid={`notif-${n.id}`}
            >
              {canManage && (
                <button
                  onClick={() => toggleSelect(n.id)}
                  className="flex-shrink-0 text-slate-400 hover:text-blue-600 transition-colors mt-1"
                  aria-label={isSelected ? "Deseleziona" : "Seleziona"}
                  data-testid={`notif-select-${n.id}`}
                >
                  {isSelected ? <CheckSquare size={16} className="text-blue-600" /> : <Square size={16} />}
                </button>
              )}
              <div className={`w-10 h-10 rounded-xl ${meta.color} flex items-center justify-center flex-shrink-0`}>
                <Bell size={18} />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-semibold text-slate-900">{n.title}</div>
                  <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${meta.color}`}>{meta.label}</span>
                </div>
                <div className="text-sm text-slate-600 mt-1">{n.message}</div>
                <div className="text-[11px] text-slate-400 mt-2">{fmtDateTime(n.sent_at)}</div>
              </div>
              {!n.is_read && (
                <button onClick={() => markOne(n.id)} className="text-xs text-blue-600 hover:text-purple-600 flex items-center gap-1" data-testid={`mark-read-${n.id}`}>
                  <Circle size={8} fill="currentColor" /> Segna letta
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
